'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@sola/auth-sdk/store.tsx';
import { formatPostalCodeLabel } from '@sola/skippy-search-sdk/lib/geo.ts';
import { fetchAutocompletePostalCode } from '@sola/skippy-search-sdk/lib/queries.ts';
import type { GeoApiResponseDto, GeoLocationDto, PostalCodeResultDto } from '@sola/skippy-search-sdk/lib/schema.ts';
export interface ResolvedLocation {
  /** Display value for the input field. */
  display?: string;
  /** Geo coordinates for location-based sorting. */
  geo?: GeoLocationDto;
  /** The source that resolved this location. */
  source: 'url' | 'browser' | 'account' | 'cloudfront' | 'default';
}

/** Build a ResolvedLocation from a PostalCodeResultDto. */
const toResolvedLocation = (r: PostalCodeResultDto, source: ResolvedLocation['source']): ResolvedLocation => ({
  display: formatPostalCodeLabel(r),
  geo: {
    city: r.city,
    state: r.state,
    latitude: r.lat,
    longitude: r.lng
  },
  source
});
const defaultResolvedLocation: ResolvedLocation = {
  display: undefined,
  geo: undefined,
  source: 'default'
};

/**
 * Resolve the passive fallback: account zip → CloudFront → Chicago default.
 * This is the location used when the user hasn't explicitly chosen one.
 */
const resolvePassiveFallback = async (userZip: string | undefined): Promise<ResolvedLocation> => {
  if (userZip) {
    const results = await fetchAutocompletePostalCode(userZip, 1);
    if (results.length > 0) {
      return toResolvedLocation(results[0], 'account');
    }
  }
  try {
    const response = await fetch('/api/geo', {
      cache: 'no-store'
    });
    if (response.ok) {
      const data: GeoApiResponseDto | null = await response.json();
      if (data?.zip && data.city && data.state && data.latitude !== undefined && data.longitude !== undefined) {
        return {
          display: `${data.zip} - ${data.city}, ${data.state}`,
          geo: {
            city: data.city,
            state: data.state,
            latitude: data.latitude,
            longitude: data.longitude
          },
          source: 'cloudfront'
        };
      }
    }
  } catch {
    // Fall through
  }
  return defaultResolvedLocation;
};
const resolveFromPostalCode = async (zip: string, source: ResolvedLocation['source']): Promise<ResolvedLocation | null> => {
  const results = await fetchAutocompletePostalCode(zip, 1);
  if (results.length > 0) {
    return toResolvedLocation(results[0], source);
  }
  return null;
};

/**
 * Resolves the user's location using a priority chain:
 * 1. URL parameter (`q` query param) — user override
 * 2. Browser geolocation (if permission already granted, or prompted via q=my-location)
 * 3. Logged-in user's preferred postal code
 * 4. CloudFront geo headers via `/api/geo`
 * 5. No location
 *
 * Returns:
 * - `resolved`: the active location (highest priority that matched)
 * - `fallback`: the passive default (account/cloudfront/chicago) for reset
 */
export const useResolveLocation = () => {
  const $ = _c(7);
  const {
    authUser
  } = useAuth("authUser");
  const [resolved, setResolved] = useState(null);
  const [fallback, setFallback] = useState(null);
  const [isResolving, setIsResolving] = useState(true);
  const hasResolvedRef = useRef(false);
  const prevAuthRef = useRef(authUser);
  let t0;
  let t1;
  if ($[0] !== authUser) {
    t0 = () => {
      const wasNull = !prevAuthRef.current;
      prevAuthRef.current = authUser;
      if (wasNull && authUser) {
        hasResolvedRef.current = false;
      }
      if (hasResolvedRef.current) {
        return;
      }
      hasResolvedRef.current = true;
      const resolve = async () => {
        const userZip = authUser?.supporter?.addressDetails?.postalCode;
        const passiveFallback = await resolvePassiveFallback(userZip);
        setFallback(passiveFallback);
        const urlQ = new URLSearchParams(globalThis.location?.search || "").get("q");
        if (urlQ && urlQ !== "my-location") {
          const loc = await resolveFromPostalCode(urlQ, "url");
          if (loc) {
            setResolved(loc);
            setIsResolving(false);
            return;
          }
        }
        if (urlQ === "my-location" || !urlQ) {
          const shouldPrompt = urlQ === "my-location";
          const permResult = shouldPrompt ? null : await navigator.permissions?.query({
            name: "geolocation"
          }).catch(_temp);
          if (shouldPrompt || permResult?.state === "granted") {
            if (shouldPrompt) {
              setResolved({
                display: "",
                geo: passiveFallback.geo,
                source: "browser"
              });
              setIsResolving(false);
              navigator.geolocation.getCurrentPosition(pos => {
                setResolved({
                  display: "",
                  geo: {
                    latitude: pos.coords.latitude,
                    longitude: pos.coords.longitude
                  },
                  source: "browser"
                });
              }, _temp2);
              return;
            }
            const position = await new Promise(_temp3);
            if (position) {
              setResolved({
                display: "",
                geo: {
                  latitude: position.coords.latitude,
                  longitude: position.coords.longitude
                },
                source: "browser"
              });
              setIsResolving(false);
              return;
            }
          }
        }
        setResolved(passiveFallback);
        setIsResolving(false);
      };
      resolve();
    };
    t1 = [authUser];
    $[0] = authUser;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== fallback || $[4] !== isResolving || $[5] !== resolved) {
    t2 = {
      resolved,
      fallback,
      isResolving
    };
    $[3] = fallback;
    $[4] = isResolving;
    $[5] = resolved;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
function _temp() {
  return null;
}
function _temp2() {}
function _temp3(res) {
  navigator.geolocation.getCurrentPosition(pos_0 => res(pos_0), () => res(null), {
    timeout: 5000
  });
}